const _ = require('lodash')

const COMBINATORS = ['allOf', 'anyOf', 'oneOf']

/**
 * @param {*} node
 * @returns {string[]}
 */
function getTypesFromSchemaNode(node) {
    if (_.has(node, ['type'])) {
        const raw = node.type
        return _.isArray(raw) ? raw : [raw]
    }

    return []
}

/**
 * @param {*} node
 * @returns {boolean}
 */
function isObjectNode(node) {
    const types = getTypesFromSchemaNode(node)
    return types.includes('object') || _.has(node, ['properties'])
}

/**
 * @param {*} node
 * @returns {boolean}
 */
function isArrayNode(node) {
    const types = getTypesFromSchemaNode(node)
    return types.includes('array') || _.has(node, ['items'])
}

/**
 * @param {*} obj
 * @returns {*}
 */
function removeTypes(obj) {
    return _.omit(obj, ['type'])
}

/**
 * @param {*} obj
 * @returns {*}
 */
function removePseudoTypes(obj) {
    return _.omit(obj, ['pseudoType'])
}

/**
 * @param {*} obj
 * @returns {*}
 */
function removeAllTypes(obj) {
    return removePseudoTypes(removeTypes(obj))
}

/**
 * @param {*} obj
 * @returns {*}
 */
function removeFormats(obj) {
    return _.omit(obj, 'format')
}

/**
 * @param {*} elements
 * @param {*} validElements
 * @returns {boolean}
 */
function allElementsAreInIncluded(elements, validElements) {
    return elements.every((/** @type {*} */ e) => validElements.includes(e))
}

/**
 * @param {*} elements
 * @param {*} validElements
 * @returns {boolean}
 */
function someElementsAreInIncluded(elements, validElements) {
    return elements.some((/** @type {*} */ e) => validElements.includes(e))
}

/**
 * @param {*} obj
 * @param {*} f
 * @param {...*} args
 * @returns {*}
 */
function mapNestedValues(obj, f, ...args) {
    return _.mapValues(obj, values => _.mapValues(values, (v, k) => f(v, k, ...args)))
}

/**
 * @param {*} collection
 * @param {*} f
 * @returns {*}
 */
function mapAndClean(collection, f) {
    return _(collection).mapValues(f).omitBy(_.isEmpty).value()
}

/**
 * @param {*} schema
 * @returns {boolean}
 */
function isObjectSchema(schema) {
    const type = _.get(schema, ['type'], [])
    const types = _.isArray(type) ? type : [type]
    if (_.includes(types, 'object') || _.has(schema, ['properties'])) {
        return true
    }
    const combinator = _.find(['allOf', 'anyOf', 'oneOf'], k => _.has(schema, k))
    if (combinator) {
        return _.some(schema[combinator], isObjectSchema)
    }
    return false
}

/**
 * @param {*} schema
 * @returns {boolean}
 */
function isObjectSchemaStrict(schema) {
    const type = _.get(schema, ['type'], [])
    const types = _.isArray(type) ? type : [type]
    if (
        (types.length && !types.includes('object')) ||
        _.has(schema, ['enum']) ||
        _.has(schema, ['items']) ||
        _.has(schema, ['minLength']) ||
        _.has(schema, ['maxLength']) ||
        _.has(schema, ['minimum']) ||
        _.has(schema, ['maximum']) ||
        _.has(schema, ['pattern']) ||
        _.has(schema, ['format']) ||
        (types?.length > 1 && !_.has(schema, ['properties']))
    ) {
        return false
    }
    if (
        type === 'object' ||
        _.includes(types, 'object') ||
        _.has(schema, ['properties']) ||
        _.has(schema, ['required']) ||
        _.has(schema, ['additionalProperties']) ||
        _.has(schema, ['patternProperties'])
    ) {
        return true
    }
    const combinator = _.find(['allOf', 'anyOf', 'oneOf'], k => _.has(schema, k))
    if (combinator) {
        return _.every(schema[combinator], _schema => isObjectSchemaStrict(_schema))
    }
    return types.length === 0
}

/**
 *
 * @param {*} schema
 * @returns {*|boolean}
 */
function isTopLevelObjectSchema(schema) {
    return schema?.isTopLevelDataNode && isObjectSchema(schema)
}

/**
 * @param {*} namespaceData
 * @param {*} namespaceName
 * @param {*} combinator
 * @returns {*}
 */
function mapDataTypesThatAreObjects(namespaceData, namespaceName, combinator) {
    const convert = (/** @type {*} */ schema, /** @type {*} */ key) =>
        isTopLevelObjectSchema(schema) ? combinator(schema, key, namespaceName) : schema
    return _.mapValues(namespaceData, convert)
}

/** Map values but do something a bit different for some of them
 *
 * Similar to lodash `_.mapValues` but allows for different treatment according to some keys
 * @example
 *      const result = mapValuesWithSpecialCases({a: 1, b: 2, c: 3}, {a: x => x + 1}, x => x * 2)
 *      result === {a: 2, b: 4, c: 6}
 *
 * @param {*} values The object to iterate
 * @param {*} specialCases A map from keys to functions
 * @param {*} normalCase A map function
 * @returns {{}}
 */
function mapValuesWithSpecialCases(values, specialCases, normalCase) {
    return _.mapValues(values, (v, k) => (specialCases[k] || normalCase)(v, k))
}

/**
 * @param {*} node
 * @returns {*}
 */
function getCombinator(node) {
    const combinators = COMBINATORS.filter(combinator => _.has(node, combinator))

    if (combinators.length > 1) {
        throw new Error(`Expected exactly one alternation keyword, got: ${combinators.length}`)
    }

    return _.head(combinators)
}

module.exports = {
    getTypesFromSchemaNode,
    isObjectNode,
    isArrayNode,
    removeTypes,
    mapNestedValues,
    mapAndClean,
    removeAllTypes,
    removeFormats,
    allElementsAreInIncluded,
    someElementsAreInIncluded,
    mapValuesWithSpecialCases,
    mapDataTypesThatAreObjects,
    isObjectSchema,
    isObjectSchemaStrict,
    getCombinator
}
